import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { TokenService } from '../../../token/token.service';
import {
  CreateProject,
  CreateProjectPreset,
  DeleteProjectPreset,
  GenerateImage,
  GenerateLivery,
  GetProjectByTemplateSlug,
  GetProjectPresets,
  GetStickers,
  GetTemplate,
  ImageUpload,
  SendAiChat,
  TranslateKoToEn,
  ChatBotReset,
  GetProjectByUuid,
  ThreedBaseModel,
  Templates,
  GetCompanyProjects,
  CreateCompanyProjects,
  EmbedProjectCreate,
  CompanyProject,
  EmbedProjectDelete,
  CompanyProjectsUpdate,
  EmbedProjectUpdate,
  GetTemplateByUuid,
  EmbedProjectAddPartModel,
  EmbedProjectDeletePartModel,
  GetEmbedProjectUuid,
  ThreedPartCategories,
  ThreedPartModels,
  ThreedPartModel,
  EmbedProjectPresetAdd,
  EmbedProjectPresetDelete,
  EmbedProjectMainPresetUpdate,
  ProjectPreset,
  CompanyProjectsDelete,
} from '..';
import {
  ActionData,
  BaseModel,
  PartCategory,
  PartModel,
  Template,
  ThreedProject,
  ThreedProjectPreset,
} from 'src/app/core/layout/layouts/workspace-layout/commons/services/project-controller/project-controller';
import { Howl, Howler } from 'howler';
import { SettingInfo } from 'src/app/core/layout/layouts/workspace-layout/commons/class/state-manager';
import { EmbedData } from 'src/app/core/layout/layouts/edit-layout/common/data/embed';
@Injectable({
  providedIn: 'root',
})
export class ThreeGraphqlService {
  public language: 'EN' | 'KO' | 'JA' | undefined;
  constructor(
    private apollo: Apollo,
    private tokenService: TokenService,
  ) {
    this.language = this.getBrowserLanguage();
  }

  public getCompanyProjects(userEmail: string, first?: number): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    const context: any = {};

    if (this.tokenService.accessToken) context['headers'] = headers;

    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetCompanyProjects,
        variables: { userEmail, first: first ? first : 30, language: this.language },
        context,
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const company = res.data.company;
          return {
            uuid: company.uuid,
            title: company.name,
            companyProjects: company.companyProjects.edges?.map((edge: any) => {
              const project = edge.node;
              return {
                cover: project.cover,
                uuid: project.uuid,
                title: project.title,
              };
            }),
          };
        }),
      );
  }

  public createCompanyProjects(companyUuid: string, title: string): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: CreateCompanyProjects,
        variables: {
          input: {
            companyUuid,
            title,
          },
        },
        context: {
          headers,
        },
      })
      .pipe(
        map((result: any) => {
          const project = result.data.companyProjectsCreate.companyProjects;
          return project;
        }),
      );
  }

  public deleteCompanyProject(uuid: string): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: CompanyProjectsDelete,
        variables: {
          uuid,
        },
        context: {
          headers,
        },
      })
      .pipe(map((result: any) => result.data.companyProjectsDelete.success));
  }

  public updateCompanyProjects(
    uuid: string,
    input: {
      title?: string;
      description?: string;
      isCapture?: boolean;
      isAutoRotation?: boolean;
      isFullScreen?: boolean;
      color?: [];
    },
  ): Observable<EmbedData> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: CompanyProjectsUpdate,
        variables: {
          uuid,
          input,
        },
        context: {
          headers,
        },
      })
      .pipe(
        map((result: any) => {
          const project = result.data.companyProjectsUpdate.companyProjects;
          return {
            uuid: project.uuid,
            title: project.title,
            cover: project.cover,
            description: project.description,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            isAutoRotation: project.isAutoRotation,
            isCapture: project.isCapture,
            isFullScreen: project.isFullScreen,
            projects:
              project.projects.edges?.map((edge: any) => {
                const embedProject = {
                  createdAt: edge.node.createdAt,
                  updatedAt: edge.node.updatedAt,
                  uuid: edge.node.uuid,
                  title: edge.node.title,
                  slug: edge.node.slug,
                  template: edge.node.template,
                  thumbnail: edge.node.thumbnail,
                  thumbnailMobile: edge.node.thumbnailMobile,
                  thumbnailWebp: edge.node.thumbnailWebp,
                  setting: edge.node.setting
                    ? {
                        colors: edge.node.setting?.colors ? [...edge.node.setting.colors] : [],
                        isColor: edge.node.setting.isColor,
                        isSticker: edge.node.setting.isSticker,
                        isPart: edge.node.setting.isPart,
                      }
                    : {},
                  colors: edge.node.colors ? [...edge.node.colors] : [],
                  partModels:
                    edge.node.partModels?.edges.map((partModelEdge: any) => {
                      return {
                        uuid: partModelEdge.node.uuid,
                        title: partModelEdge.node.title,
                        thumbnail: partModelEdge.node.thumbnail,
                      };
                    }) || [],
                };

                return embedProject;
              }) || [],
          };
        }),
      );
  }

  public updateCompanyProjectToFile(
    uuid: string,
    input: {
      title?: string;
      description?: string;
      isCapture?: boolean;
      isAutoRotation?: boolean;
      isFullScreen?: boolean;
      color?: [];
      cover?: File;
    },
  ): Observable<EmbedData> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .mutate({
        mutation: CompanyProjectsUpdate,
        variables: {
          uuid,
          input: {
            title: input.title,
            description: input.description,
            isCapture: input.isCapture,
            isAutoRotation: input.isAutoRotation,
            isFullScreen: input.isFullScreen,
            color: input.color,
            cover: input.cover,
          },
        },
        context: {
          headers,
          useMultipart: true,
        },
      })
      .pipe(
        map((result: any) => {
          const project = result.data.companyProjectsUpdate.companyProjects;
          return {
            uuid: project.uuid,
            title: project.title,
            cover: project.cover,
            description: project.description,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            isAutoRotation: project.isAutoRotation,
            isCapture: project.isCapture,
            isFullScreen: project.isFullScreen,
            projects:
              project.projects.edges?.map((edge: any) => {
                const embedProject = {
                  createdAt: edge.node.createdAt,
                  updatedAt: edge.node.updatedAt,
                  uuid: edge.node.uuid,
                  title: edge.node.title,
                  slug: edge.node.slug,
                  template: edge.node.template,
                  thumbnail: edge.node.thumbnail,
                  thumbnailMobile: edge.node.thumbnailMobile,
                  thumbnailWebp: edge.node.thumbnailWebp,
                  setting: edge.node.setting
                    ? {
                        colors: edge.node.setting?.colors ? [...edge.node.setting.colors] : [],
                        isColor: edge.node.setting.isColor,
                        isSticker: edge.node.setting.isSticker,
                        isPart: edge.node.setting.isPart,
                      }
                    : {},
                  colors: edge.node.colors ? [...edge.node.colors] : [],
                  partModels:
                    edge.node.partModels?.edges.map((partModelEdge: any) => {
                      return {
                        uuid: partModelEdge.node.uuid,
                        title: partModelEdge.node.title,
                        thumbnail: partModelEdge.node.thumbnail,
                      };
                    }) || [],
                };

                return embedProject;
              }) || [],
          };
        }),
      );
  }

  public updateEmbedProjectMainPreset(
    projectUuid: string,
    presetUuid: string,
    first?: number,
  ): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectMainPresetUpdate,
        variables: {
          projectUuid,
          presetUuid,
          first: first ? first : 100,
          language: this.language,
        },
      })
      .pipe(
        map((res: any) => {
          const embedProject = res.data.embedProjectMainPresetUpdate.project;
          return {
            uuid: embedProject.uuid,
            title: embedProject.title,
            slug: embedProject.slug,
            template: embedProject.template,
            thumbnail: embedProject.thumbnail,
            thumbnailMobile: embedProject.thumbnailMobile,
            thumbnailWebp: embedProject.thumbnailWebp,
            setting: {
              isColor: embedProject.setting?.isColor || false,
              isSticker: embedProject.setting?.isSticker || false,
              isPart: embedProject.setting?.isPart || false,
              colors: embedProject.setting?.colors ? [...embedProject.setting.colors] : [],
            },
            colors: embedProject.colors ? [...embedProject.colors] : [],
            partModels:
              embedProject.partModels?.edges?.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
            presets: embedProject.presets?.edges?.map((edge: any) => edge.node) || [],
            mainPreset: embedProject.mainPreset,
          };
        }),
      );
  }

  public getCompanyProjectByUuid(uuid: string): Observable<EmbedData | undefined> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .watchQuery({
        query: CompanyProject,
        variables: { uuid, first: 20 },
        context: {
          headers,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const project = res.data.companyProject;

          return {
            uuid: project.uuid,
            cover: project.cover,
            title: project.title,
            description: project.description,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            isAutoRotation: project.isAutoRotation,
            isCapture: project.isCapture,
            isFullScreen: project.isFullScreen,
            projects:
              project.projects.edges?.map((edge: any) => {
                const embedProject = {
                  createdAt: edge.node.createdAt,
                  updatedAt: edge.node.updatedAt,
                  uuid: edge.node.uuid,
                  title: edge.node.title,
                  slug: edge.node.slug,
                  presets: project.preset?.edges?.map((edge: any) => edge.node) || [],
                  mainPreset: project.mainPreset,
                  template: edge.node.template,
                  thumbnail: edge.node.thumbnail,
                  thumbnailMobile: edge.node.thumbnailMobile,
                  thumbnailWebp: edge.node.thumbnailWebp,
                  setting: edge.node.setting
                    ? {
                        colors: edge.node.setting?.colors ? [...edge.node.setting.colors] : [],
                        isColor: edge.node.setting.isColor,
                        isSticker: edge.node.setting.isSticker,
                        isPart: edge.node.setting.isPart,
                      }
                    : {},
                  colors: edge.node.colors ? [...edge.node.colors] : [],
                  partModels:
                    edge.node.partModels?.edges?.map((partModelEdge: any) => {
                      return {
                        uuid: partModelEdge.node.partModel.uuid,
                        title: partModelEdge.node.partModel.title,
                        thumbnail: partModelEdge.node.partModel.thumbnail,
                      };
                    }) || [],
                };

                return embedProject;
              }) || [],
          };
        }),
      );
  }

  public addEmebedProjectPreset(
    projectUuid: string,
    presetUuids: string[],
    first?: number,
  ): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectPresetAdd,
        variables: {
          projectUuid,
          presetUuids,
          first: first ? first : 100,
          language: this.language,
        },
      })
      .pipe(
        map((res: any) => {
          const embedProject = res.data.embedProjectPresetAdd.project;
          return {
            uuid: embedProject.uuid,
            title: embedProject.title,
            slug: embedProject.slug,
            template: embedProject.template,
            thumbnail: embedProject.thumbnail,
            thumbnailMobile: embedProject.thumbnailMobile,
            thumbnailWebp: embedProject.thumbnailWebp,
            setting: {
              isColor: embedProject.setting?.isColor || false,
              isSticker: embedProject.setting?.isSticker || false,
              isPart: embedProject.setting?.isPart || false,
              colors: embedProject.setting?.colors ? [...embedProject.setting.colors] : [],
            },
            colors: embedProject.colors ? [...embedProject.colors] : [],
            partModels:
              embedProject.partModels?.edges?.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
            presets: embedProject.presets?.edges?.map((edge: any) => edge.node) || [],
            mainPreset: embedProject.mainPreset,
          };
        }),
      );
  }

  public deleteEmebedProjectPreset(uuid: string): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo.use('threed').mutate({
      mutation: EmbedProjectPresetDelete,
      variables: {
        uuid,
      },
      context: {
        headers,
      },
    });
  }

  public getTempletes(first: number, after?: string): Observable<Array<any>> {
    return this.apollo
      .use('threed')
      .watchQuery({
        query: Templates,
        variables: { first, after },
      })
      .valueChanges.pipe(
        map((result: any) => {
          const templetes: Array<any> = result.data.templates.edges.map((edge: any) => {
            const templete: any = {
              uuid: edge.node.uuid,
              title: edge.node.title,
              slug: edge.node.slug,
              description: edge.node.description,
              thumbnail: edge.node.thumbnail,
              thumbnailMobile: edge.node.thumbnailMobile,
              thumbnailVideo: edge.node.thumbnailVideo,
              thumbnailWebp: edge.node.thumbnailWebp,
              createdAt: edge.node.createdAt,
              updatedAt: edge.node.updatedAt,
              cursor: edge.cursor,
              isSelected: false,
            };
            return templete;
          });
          return templetes;
        }),
      );
  }

  public getTempleteByUuid(uuid: string, first?: number): Observable<Template | undefined> {
    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetTemplateByUuid,
        variables: { uuid, first: first ? first : 40, language: this.language },
        fetchPolicy: 'cache-and-network',
      })
      .valueChanges.pipe(
        map((result: any) => {
          const templete: Template = {
            uuid: result.data.template.uuid,
            title: result.data.template.title,
            slug: result.data.template.slug,
            description: result.data.template.description,
            thumbnail: result.data.template.thumbnail,
            thumbnailVideo: result.data.template.thumbnailVideo,
            option: result.data.template.option,
            createdAt: result.data.template.createdAt,
            updatedAt: result.data.template.updatedAt,
            partCategories: this.flatCategories(
              result.data.template.partCategories.edges.map(this.mapPartCategory.bind(this)),
            ),
          };
          return templete;
        }),
      );
  }

  public getTemplete(slug: string): Observable<Template | undefined> {
    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetTemplate,
        variables: { slug },
      })
      .valueChanges.pipe(
        map((result: any) => {
          const templete: Template = {
            uuid: result.data.template.uuid,
            title: result.data.template.title,
            slug: result.data.template.slug,
            description: result.data.template.description,
            thumbnail: result.data.template.thumbnail,
            thumbnailVideo: result.data.template.thumbnailVideo,
            option: result.data.template.option,
            createdAt: result.data.template.createdAt,
            updatedAt: result.data.template.updatedAt,
          };

          return templete;
        }),
        catchError((error) => {
          confirm('템플릿을 불러오는데 실패했습니다.');
          return of(undefined);
        }),
      );
  }

  public getEmbedProjectByUuid(uuid: string, first?: number): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetEmbedProjectUuid,
        variables: {
          uuid,
          first: first ? first : 40,
          language: this.language,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const project = res.data.embedProject;
          return {
            uuid: project.uuid,
            title: project.title,
            slug: project.slug,
            template: project.template,
            thumbnail: project.thumbnail,
            thumbnailMobile: project.thumbnailMobile,
            thumbnailWebp: project.thumbnailWebp,
            presets: project.presets?.edges?.map((edge: any) => edge.node) || [],
            mainPreset: project.mainPreset,
            setting: {
              isColor: project.setting?.isColor || false,
              isSticker: project.setting?.isSticker || false,
              isPart: project.setting?.isPart || false,
              colors: project.setting?.colors ? [...project.setting.colors] : [],
            },
            colors: project.colors ? [...project.colors] : [],
            baseModels:
              project.baseModels?.edges?.map((edge: any) => {
                const baseModel = edge.node.baseModel;
                return this.mapBaseModel(baseModel);
              }) || [],
            partModels:
              project.partModels?.edges?.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
          };
        }),
      );
  }

  public createEmbedProject(
    templateUuid: string,
    companyProjectsUuid: string,
    first?: number,
  ): Observable<any> {
    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectCreate,
        variables: {
          templateUuid,
          companyProjectsUuid,
          first: first ? first : 40,
          language: this.language,
        },
        context: {},
      })
      .pipe(
        map((result: any) => {
          const embedProject = result.data.embedProjectCreate.project;
          return {
            uuid: embedProject.uuid,
            title: embedProject.title,
            slug: embedProject.slug,
            template: embedProject.template,
            thumbnail: embedProject.thumbnail,
            thumbnailMobile: embedProject.thumbnailMobile,
            thumbnailWebp: embedProject.thumbnailWebp,
            presets: embedProject.presets?.edges?.map((edge: any) => edge.node) || [],
            mainPreset: embedProject.mainPreset,
            setting: {
              isColor: embedProject.setting?.isColor || false,
              isSticker: embedProject.setting?.isSticker || false,
              isPart: embedProject.setting?.isPart || false,
              colors: embedProject.setting?.colors ? [...embedProject.setting.colors] : [],
            },
            colors: embedProject.colors ? [...embedProject.colors] : [],
            partModels:
              embedProject.partModels?.edges?.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
          };
        }),
      );
  }

  public deleteEmbedProject(uuid: string): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo.use('threed').mutate({
      mutation: EmbedProjectDelete,
      variables: {
        uuid,
      },
      context: {
        headers,
      },
    });
  }

  public updateEmbedProject(
    uuid: string,
    setting?: {
      isColor?: boolean;
      isSticker?: boolean;
      isPart?: boolean;
      colors?: string[];
    },
    first?: number,
  ): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectUpdate,
        variables: {
          uuid,
          first: first ? first : 40,
          language: this.language,
          input: {
            setting: {
              isColor: setting?.isColor || false,
              isSticker: setting?.isSticker || false,
              isPart: setting?.isPart || false,
            },
            colors: setting?.colors ? [...setting.colors] : [],
          },
        },
        context: {
          headers,
        },
      })
      .pipe(
        map((result: any) => {
          const embedProject = result.data.embedProjectUpdate.project;
          return {
            uuid: embedProject.uuid,
            title: embedProject.title,
            slug: embedProject.slug,
            template: embedProject.template,
            thumbnail: embedProject.thumbnail,
            thumbnailMobile: embedProject.thumbnailMobile,
            thumbnailWebp: embedProject.thumbnailWebp,
            presets: embedProject.presets?.edges?.map((edge: any) => edge.node) || [],
            mainPreset: embedProject.mainPreset,
            setting: {
              isColor: embedProject.setting?.isColor || false,
              isSticker: embedProject.setting?.isSticker || false,
              isPart: embedProject.setting?.isPart || false,
              colors: embedProject.setting?.colors ? [...embedProject.setting.colors] : [],
            },
            colors: embedProject.colors ? [...embedProject.colors] : [],
            partModels:
              embedProject.partModels?.edges?.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
          };
        }),
      );
  }

  public addPartModelInProject(
    projectUuid: string,
    partModelUuid: string,
    first?: number,
  ): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectAddPartModel,
        variables: {
          projectUuid,
          partModelUuid,
          first: first ? first : 40,
          language: this.language,
        },
        context: {
          headers,
        },
      })
      .pipe(
        map((res: any) => {
          const project = res.data.embedProjectAddPartModel.project;

          return {
            uuid: project.uuid,
            title: project.title,
            slug: project.slug,
            template: project.template,
            thumbnail: project.thumbnail,
            thumbnailMobile: project.thumbnailMobile,
            thumbnailWebp: project.thumbnailWebp,
            setting: {
              isColor: project.setting?.isColor || false,
              isSticker: project.setting?.isSticker || false,
              isPart: project.setting?.isPart || false,
              colors: project.setting?.colors ? [...project.setting.colors] : [],
            },
            colors: project.colors ? [...project.colors] : [],
            partModels:
              project.partModels?.edges.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
          };
        }),
      );
  }

  public deletePartModelInProject(
    projectUuid: string,
    partModelUuid: string,
    first?: number,
  ): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .mutate({
        mutation: EmbedProjectDeletePartModel,
        variables: {
          projectUuid,
          partModelUuid,
          first: first ? first : 40,
          language: this.language,
        },
      })
      .pipe(
        map((res: any) => {
          const project = res.data.embedProjectDeletePartModel.project;

          return {
            uuid: project.uuid,
            title: project.title,
            slug: project.slug,
            template: project.template,
            thumbnail: project.thumbnail,
            thumbnailMobile: project.thumbnailMobile,
            thumbnailWebp: project.thumbnailWebp,
            setting: {
              isColor: project.setting?.isColor || false,
              isSticker: project.setting?.isSticker || false,
              isPart: project.setting?.isPart || false,
              colors: project.setting?.colors ? [...project.setting.colors] : [],
            },
            colors: project.colors ? [...project.colors] : [],
            partModels:
              project.partModels?.edges.map((partModelEdge: any) => {
                return {
                  uuid: partModelEdge.node.partModel.uuid,
                  title: partModelEdge.node.partModel.title,
                  thumbnail: partModelEdge.node.partModel.thumbnail,
                };
              }) || [],
          };
        }),
      );
  }

  public createProeject(
    templateUuid: string,
    first?: number,
  ): Observable<ThreedProject | undefined> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: CreateProject,
        variables: {
          templateUuid,
          first: first ? first : 40,
          language: this.language,
        },
        context: {
          headers,
        },
      })
      .pipe(
        map((res: any) => {
          const { project } = res.data.projectCreate;
          return {
            uuid: project.uuid,
            title: project.title,
            templateUuid: project.template.uuid,
            description: project.description,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            baseModel: this.mapBaseModel(project.baseModel),
            partCategories: this.flatCategories(
              project.partCategories?.edges?.map(this.mapPartCategory.bind(this)),
            ),
          };
        }),
        catchError((error) => {
          confirm('프로젝트를 생성하는데 실패했습니다.');
          return of(undefined);
        }),
      );
  }

  public searchUserProejectByTempleteSlug(templateSlug: string, first?: number): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    const context: any = {};

    if (this.tokenService.accessToken) context['headers'] = headers;

    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetProjectByTemplateSlug,
        variables: { templateSlug, first: first ? first : 5 },
        context,
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const { project } = res.data;

          return {
            uuid: project.uuid,
            title: project.title,
            description: project.description,
            templateUuid: project.template.uuid,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            baseModels: project.baseModels?.edges?.map((edge: any) => {
              const baseModel = edge.node.baseModel;
              return this.mapBaseModel(baseModel);
            }),
          };
        }),
        catchError((error) => {
          return of(undefined);
        }),
      );
  }

  public searchUserProejectByUuid(uuid: string, first?: number): Observable<any> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    const context: any = {};

    if (this.tokenService.accessToken) context['headers'] = headers;

    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetProjectByUuid,
        variables: { uuid: uuid, first: first ? first : 5 },
        context,
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const { project } = res.data;

          return {
            uuid: project.uuid,
            title: project.title,
            description: project.description,
            thumbnail: project.thumbnail,
            createdAt: project.createdAt,
            updatedAt: project.updatedAt,
            baseModels: project.baseModels?.edges.map((edge: any) => {
              const baseModel = edge.node.baseModel;
              return this.mapBaseModel(baseModel);
            }),
            partCategories: this.flatCategories(
              project.partCategories?.edges?.map(this.mapPartCategory.bind(this)),
            ), // this 바인딩
          };
        }),
        catchError((error) => {
          confirm('프로젝트를 불러오는데 실패했습니다.');
          console.error(error);
          return of(undefined);
        }),
      );
  }

  public getBaseModel(uuid: string): Observable<BaseModel | undefined> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .watchQuery({
        query: ThreedBaseModel,
        variables: { uuid },
        context: {
          headers,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          return this.mapBaseModel(res.data.threedBaseModel);
        }),
      );
  }

  private mapBaseModel(baseModel: any): BaseModel {
    const model = {
      uuid: baseModel.uuid,
      createdAt: baseModel.createdAt,
      thumbnail: baseModel.thumbnail,
      title: baseModel.title,
      updatedAt: baseModel.updatedAt,
      width: baseModel.width,
      height: baseModel.height,
      _d: baseModel._d,
      _f: baseModel._f,
      _o: baseModel._o,
      _u: baseModel._u,
      threedFile: baseModel.threedFile,
      actions: this.getActionData(baseModel.actions),
      category: 'base',
      child: [],
    };

    model['child'] =
      baseModel.materialGroups?.map((node: any) => this.traverseNodes(node, model)) || [];

    return model;
  }

  private mapPartCategory(partCategory: any) {
    const { node } = partCategory;

    const subCategories: Array<any> = node.partModels?.edges?.reduce((acc: any[], edge: any) => {
      if (!edge.node.subCategories?.edges?.length) return acc;

      const mappedSubCategories = edge.node.subCategories.edges.map((subCategory: any) => ({
        uuid: subCategory.node.uuid,
        title: subCategory.node.title,
        icon: subCategory.node.icon,
        slug: subCategory.node.slug,
        cameraInfo: {
          position: JSON.parse(subCategory.node.cameraInfo?.position || undefined),
          rotation: JSON.parse(subCategory.node.cameraInfo?.rotation || undefined),
          target: JSON.parse(subCategory.node.cameraInfo?.target || undefined),
        },
        createdAt: subCategory.node.createdAt,
        updatedAt: subCategory.node.updatedAt,
        position: subCategory.node.position,
        rotation: subCategory.node.rotation,
        target: subCategory.node.target,
        partModels:
          subCategory.node.partModels?.edges?.map(
            this.mapPartModel(node, edge.node.uuid).bind(this),
          ) || [],
      }));

      return acc.concat(mappedSubCategories);
    }, []);

    // 🔥 같은 title을 가진 subCategory들을 병합 (partModels 합치기)
    const mergedSubCategories =
      subCategories?.reduce((acc: any[], subCategory) => {
        const existing = acc.find((item) => item.title === subCategory.title);

        if (existing) {
          // 기존 subCategory가 있으면 partModels를 합침
          existing.partModels.push(...subCategory.partModels);
        } else {
          // 없으면 새로 추가
          acc.push({ ...subCategory });
        }

        return acc;
      }, []) || [];

    return [
      {
        uuid: node.uuid,
        title: node.title,
        icon: node.icon,
        slug: node.slug,
        cameraInfo: {
          position: JSON.parse(node.cameraInfo?.position || '{}'),
          rotation: JSON.parse(node.cameraInfo?.rotation || '{}'),
          target: JSON.parse(node.cameraInfo?.target || '{}'),
        },
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
        position: node.position,
        rotation: node.rotation,
        target: node.target,
        partModels: node.partModels?.edges?.map(this.mapPartModel(node).bind(this)) || [], // this 바인딩
      },
      ...mergedSubCategories,
    ];
  }

  private flatCategories(categories: any[][]): any[] {
    if (!categories) return [];
    // 1️⃣ 2차원 배열을 1차원 배열로 변환
    const flattened = categories.flat();

    // 2️⃣ 같은 title을 가진 카테고리 병합 (partModels 합치기)
    const mergedCategories = flattened.reduce((acc: any[], category: any) => {
      const existing = acc.find((item) => item.title === category.title);

      if (existing) {
        // 기존 카테고리가 있으면 partModels 병합
        existing.partModels.push(...category.partModels);
      } else {
        // 없으면 새로 추가
        acc.push({ ...category });
      }

      return acc;
    }, []);

    return mergedCategories;
  }

  private mapPartModel(category: any, parentPartModel?: string) {
    return (partModel: any) => {
      const { node } = partModel;

      return {
        uuid: node.uuid,
        title: node.title,
        thumbnail: node.thumbnail,
        _d: node._d,
        _f: node._f,
        _o: node._o,
        _u: node._u,
        sound: this.createSound(node.audio),
        actions: this.getActionData(node.actions),
        threedFile: node.threedFile,
        position: category.cameraInfo?.target,
        target: category.cameraInfo?.position,
        marketplaceProduct: {
          channel: node.marketplaceProduct?.channel,
          variantId: node.marketplaceProduct?.variantId,
          slug: node.marketplaceProduct?.slug,
          price: node.marketplaceProduct?.price,
        },
        createdAt: node.createdAt,
        updatedAt: node.updatedAt,
        category: category.slug?.replace('_', ' ') || '',
        parentPartModels: [parentPartModel],
        saleorSlug: node.saleorSlug,
        saleorPrice: node.saleorPrice,
        saleorVariantId: node.saleorVariantId,
      };
    };
  }

  public getThreedPartCategories(
    templeteUuid: string,
    first?: number,
  ): Observable<Array<PartCategory> | undefined> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });
    return this.apollo
      .use('threed')
      .watchQuery({
        query: ThreedPartCategories,
        variables: {
          templateUuid: templeteUuid,
          first: first ? first : 40,
          language: this.language,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const categories = this.flatCategories(
            res.data.threedPartCategories.edges.map(this.mapPartCategory.bind(this)),
          );

          return categories;
        }),
      );
  }

  public getPartModel(partModelUuid: string, first?: number): Observable<PartModel | undefined> {
    const headers = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .watchQuery({
        query: ThreedPartModel,
        variables: {
          uuid: partModelUuid,
          language: this.language,
          first: first ? first : 100,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          console.log(res.data.threedPartModel);

          const partModel: PartModel = {
            uuid: res.data.threedPartModel.uuid,
            title: res.data.threedPartModel.title,
            thumbnail: res.data.threedPartModel.thumbnail,
            category: res.data.threedPartModel.category.slug.replace('_', ' '),
            _d: res.data.threedPartModel._d,
            _f: res.data.threedPartModel._f,
            _o: res.data.threedPartModel._o,
            _u: res.data.threedPartModel._u,
            actions: this.getActionData(res.data.threedPartModel.actions),
            threedFile: res.data.threedPartModel.threedFile,
            position: res.data.threedPartModel.category.cameraInfo?.target,
            target: res.data.threedPartModel.category.cameraInfo?.position,
            createdAt: res.data.threedPartModel.createdAt,
            updatedAt: res.data.threedPartModel.updatedAt,
          };

          return partModel;
        }),
      );
  }

  public getPartModels(category: PartCategory): Observable<Array<PartModel> | undefined> {
    const header = new Headers({ Authorization: `Bearer ${this.tokenService.accessToken}` });

    return this.apollo
      .use('threed')
      .watchQuery({
        query: ThreedPartModels,
        variables: {
          categoryUuid: category.uuid,
          first: 100,
          language: this.language,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          if (res.errors?.length > 0) return undefined;

          const partModels = res.data.threedPartModels.edges.map(
            this.mapPartModel(category).bind(this),
          );
          return partModels;
        }),
      );
  }

  public createProjectPreset(projectUuid: string, preset: any): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: CreateProjectPreset,
        variables: {
          projectUuid: projectUuid,
          input: {
            title: preset.title,
            partModelUuids: preset.partModels,
            stickers: preset.stickers,
            thumbnail: preset.thumbnail,
            setting: preset.setting,
          },
        },
        context: {
          headers,
          useMultipart: true,
        },
      })
      .pipe(
        map((result: any) => {
          const preset: ThreedProjectPreset = {
            uuid: result.data.projectPresetCreate.projectPreset.uuid,
            title: result.data.projectPresetCreate.projectPreset.title,
            thumbnail: result.data.projectPresetCreate.projectPreset.thumbnail,
            stickers:
              result.data.projectPresetCreate.projectPreset.stickerPresets?.map(
                (sticker: any) => sticker.setting,
              ) || [],
            setting: result.data.projectPresetCreate.projectPreset.setting,
            partModelUuids:
              result.data.projectPresetCreate.projectPreset.partModelPresets?.map(
                (part: any) => part.partModel.uuid,
              ) || [],
          };
          return preset;
        }),
      );
  }

  public getPreset(uuid: string): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .watchQuery({
        query: ProjectPreset,
        variables: {
          uuid,
        },
      })
      .valueChanges.pipe(
        map((res: any) => {
          const prest = {
            uuid: res.data.projectPreset.uuid,
            title: res.data.projectPreset.title,
            thumbnail: res.data.projectPreset.thumbnail,
            stickers:
              res.data.projectPreset.stickerPresets?.map((sticker: any) => sticker.setting) || [],
            setting: res.data.projectPreset.setting,
            partModelUuids:
              res.data.projectPreset.partModelPresets?.map((part: any) => part.partModel.uuid) ||
              [],
          };

          return prest;
        }),
      );
  }

  public getProjectPresets(projectUuid: string | undefined): Observable<ThreedProjectPreset[]> {
    if (!projectUuid) return of([]);
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .watchQuery({
        query: GetProjectPresets,
        variables: {
          projectUuid,
        },
        context: {
          headers,
          useMultipart: true,
        },
        fetchPolicy: 'cache-and-network', // 캐시와 네트워크 요청 병행
      })
      .valueChanges.pipe(
        map((result: any) =>
          result.data.projectPresets?.edges
            ?.filter((edge: any) => edge.node.thumbnail)
            .map((edge: any) => {
              return {
                uuid: edge.node.uuid,
                title: edge.node.title,
                thumbnail: edge.node.thumbnail,
                stickers: edge.node.stickerPresets?.map((sticker: any) => sticker.setting) || [],
                setting: edge.node.setting,
                partModelUuids:
                  edge.node.partModelPresets?.map((part: any) => part.partModel.uuid) || [],
              };
            }),
        ),
      );
  }

  public deletePreset(uuid: string): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: DeleteProjectPreset,
        variables: {
          uuid,
        },
        context: {
          headers,
        },
      })
      .pipe(map((result: any) => result.data.projectPresetDelete));
  }

  public getStickers(): Observable<any> {
    return this.apollo
      .use('threed')
      .watchQuery({ query: GetStickers })
      .valueChanges.pipe(
        map((result: any) =>
          result.data.stickers.edges
            ? result.data.stickers.edges.map((node: any) => node.node)
            : [],
        ),
      );
  }

  public traverseNodes(node: any, parent?: any): any {
    const data: any = {};
    if (!node.child || node.child.length === 0) {
      data['description'] = node.description;
      data['materials'] = node.materials;
      data['position'] = JSON.parse(node.cameraInfo?.position || 'null');
      data['rotation'] = JSON.parse(node.cameraInfo?.rotation || 'null');
      data['target'] = JSON.parse(node.cameraInfo?.target || 'null');
      data['thumbnail'] = node.thumbnail;
      data['title'] = `${node.title}`;
      data['originalName'] = node.materialFullName;
      data['name'] = node.materialFullName.split('.')[1] || node.materialFullName.split('_')[1];
      data['uuid'] = node.id;
      data['parent'] = parent;
      data['child'] = [];
      return data;
    } else {
      data['description'] = node.description;
      data['materials'] = node.materials;
      data['position'] = JSON.parse(node.cameraInfo?.position || 'null');
      data['rotation'] = JSON.parse(node.cameraInfo?.rotation || 'null');
      data['target'] = JSON.parse(node.cameraInfo?.target || 'null');
      data['thumbnail'] = node.thumbnail;
      data['title'] = `${node.title}`;
      data['originalName'] = node.materialFullName;
      data['name'] = node.materialFullName.split('.')[1] || node.materialFullName.split('_')[1];
      data['uuid'] = node.id;
      data['parent'] = parent;
      data['child'] = node.child.map((_node: any) => this.traverseNodes(_node, data));
      return data;
    }
  }

  public uploadImage(file: any): Observable<string> {
    return this.apollo
      .use('threed')
      .mutate({
        mutation: ImageUpload,
        variables: {
          image: file,
        },
        context: {
          useMultipart: true,
        },
      })
      .pipe(map((result: any) => result.data.uploadImage));
  }

  public transKoToEn(text: string): Observable<string> {
    return this.apollo
      .use('threed')
      .watchQuery({
        query: TranslateKoToEn,
        variables: {
          text,
        },
      })
      .valueChanges.pipe(map((result: any) => result.data.translateText));
  }

  public sendAiChat(
    text: string,
    projectUuid: string | undefined,
    settings?: SettingInfo[],
  ): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .watchQuery({
        query: SendAiChat,
        variables: {
          message: text,
          projectUuid,
          settings,
        },
        context: {
          errorPolicy: '',
          headers,
        },
        errorPolicy: 'all',
        fetchPolicy: 'network-only', // 🔥 네트워크에서만 데이터 가져오기
      })
      .valueChanges.pipe(
        map((result: any) => {
          return {
            message: result.data.chatBot.message.replace(/\n/g, '<br>'),
            partModelUuids: result.data.chatBot.partModelUuids,
            setting: result.data.chatBot.setting,
          };
        }),
        catchError((error) => {
          return of({
            message: '죄송합니다. 요청을 제대로 수행 할 수가 없습니다. 다시한번 말씀해주세요.',
          });
        }),
      );
  }

  public generateImage(prompt: string): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: GenerateImage,
        variables: {
          prompt,
        },
        context: {
          headers,
        },
      })
      .pipe(map((result: any) => result.data.imageGenerate));
  }

  public generateLivery(prompt: string): Observable<any> {
    const headers = new Headers({
      Authorization: `Bearer ${this.tokenService.accessToken}`,
    });
    return this.apollo
      .use('threed')
      .mutate({
        mutation: GenerateLivery,
        variables: {
          prompt,
        },
        context: {
          headers,
        },
      })
      .pipe(map((result: any) => result.data.liveryGenerate));
  }

  public getActionData(actions: any): ActionData[] {
    if (!actions) return [];
    const actionDataList: ActionData[] = [];
    const moves = actions.meshMoveAction?.edges.map((edge: any) => {
      return {
        id: edge.node.id,
        type: 'move',
        target: edge.node.targetMesh.map((target: any) => target.name),
        move: {
          x: edge.node.x,
          y: edge.node.y,
          z: edge.node.z,
        },
      };
    });
    const rotations = actions.meshRotateAction?.edges.map((edge: any) => {
      return {
        type: 'rotate',
        target: edge.node.targetMesh.map((target: any) => target.name),
        rotation: {
          x: edge.node.x,
          y: edge.node.y,
          z: edge.node.z,
        },
      };
    });
    if (moves) actionDataList.push(...moves);
    if (rotations) actionDataList.push(...rotations);
    return actionDataList;
  }

  public resetChatbot(projectUuid: string): Observable<boolean> {
    return this.apollo
      .use('threed')
      .watchQuery({
        query: ChatBotReset,
        variables: {
          projectUuid,
        },
      })
      .valueChanges.pipe(map((result: any) => result.data.chatBotReset));
  }

  private createSound(soundUrl: string): Howl | undefined {
    if (!soundUrl) return undefined;
    return new Howl({
      src: [soundUrl],
      volume: 1.0,
      loop: false,
      rate: 1.0,
    });
  }

  private getBrowserLanguage(): 'EN' | 'KO' | 'JA' {
    const lang = navigator.language || navigator.languages[0] || 'en';
    const shortLang = lang.split('-')[0];

    switch (shortLang) {
      case 'ko':
        return 'KO';
      case 'ja':
        return 'JA';
      case 'en':
        return 'EN';
      default:
        return 'EN';
    }
  }
}
